import type * as openapi from '@/helpers/api/types/openapiOverrides';

import { computed, reactive } from 'vue';
import { defineStore } from 'pinia';
import { cloneDeep, isEqual } from 'lodash';

import { useDialogStore } from '@/stores/useDialogStore';
import { useJobEditAddressBookStore } from '@/stores/useJobEditAddressBookStore';
import * as fetchers from '@/helpers/fetchers';
import {
  KJobEditError,
  KJobEditLockNotFoundError,
  KJobEditLockedByOtherError,
} from '@/helpers/throwables';
import { createEmptyStopItem } from '@/models/jobEdit';

interface JobEditLockState {
  isLocked: boolean;
  isCurrentSection: boolean;
  lockedBy: string | null;
  lockTimestamp: number | null;
  currentSection: string | null;
}

interface OverlayFeedbackState {
  sectionName: string | null;
  tick: number;
}

export type JobData = openapi.JobeditGetJobContentOpenapi;

interface PrivateJobEditState {
  jobId: number | null;
  jobDate: string | null;
  isLocking: boolean;
  isLoading: boolean;
  error: Error | null;
  jobData: JobData | null;
  originalJobData: JobData | null;
  lockState: JobEditLockState;
  overlayFeedback: OverlayFeedbackState;
}

const defaultPrivateJobEditState: PrivateJobEditState = {
  jobId: null,
  jobDate: null,
  isLocking: false,
  isLoading: false,
  error: null,
  jobData: null,
  originalJobData: null,
  lockState: {
    isLocked: false,
    isCurrentSection: false,
    lockedBy: null,
    lockTimestamp: null,
    currentSection: null,
  },
  overlayFeedback: {
    sectionName: null,
    tick: 0,
  },
};

const usePrivateJobEditStore = defineStore('_jobEdit', () => {
  const state = reactive<PrivateJobEditState>(
    cloneDeep(defaultPrivateJobEditState),
  );

  function $reset(): void {
    Object.assign(state, cloneDeep(defaultPrivateJobEditState));
  }
  function init(id: number, date: string): void {
    state.jobId = id;
    state.jobDate = date;
  }

  function updateJobData(data: Partial<JobData>): void {
    if (!state.jobData) return;
    Object.assign(state.jobData, data);

    if (!state.originalJobData) {
      state.originalJobData = cloneDeep(state.jobData);
    }
  }

  const hasChanges = computed(() => {
    if (!state.originalJobData || !state.jobData) return false;
    return !isEqual(state.originalJobData, state.jobData);
  });

  return {
    state,
    $reset,
    init,
    updateJobData,
    hasChanges,
  };
});

export type OnDataChangedCallback = (() => Promise<void>) | null;

export const useJobEditStore = defineStore('jobEdit', () => {
  const privateStore = usePrivateJobEditStore();
  const { state } = privateStore;
  const dialogStore = useDialogStore();

  let loadJobRequestId = 0;
  let onDataChangedCallback: OnDataChangedCallback = null;

  const addressBookStore = useJobEditAddressBookStore();

  function setOnDataChanged(callback: OnDataChangedCallback): void {
    onDataChangedCallback = callback;
  }

  async function notifyDataChanged(): Promise<void> {
    await onDataChangedCallback?.();
  }

  const jobId = computed(() => state.jobId);
  const jobDate = computed(() => state.jobDate);
  const jobData = computed(() => state.jobData);
  const originalJobData = computed(() => state.originalJobData);
  const isLoading = computed(() => state.isLoading);
  const isLocking = computed(() => state.isLocking);

  const hasChanges = computed(() => privateStore.hasChanges);

  function init(id: number, date: string): void {
    privateStore.init(id, date);
  }

  async function lockJob(): Promise<void> {
    if (state.lockState.isLocked || !state.jobId || !state.jobDate) {
      return;
    }

    state.isLocking = true;
    try {
      const response = await fetchers.fetchPostJobEditLock({
        job_id: state.jobId,
        job_date: state.jobDate,
      });

      Object.assign(state.lockState, {
        isLocked: true,
        lockedBy: response.locked_by || 'me',
        lockTimestamp: Date.now(),
        isCurrentSection: true,
      });
    } catch (error) {
      const jobEditError =
        error instanceof Error
          ? error
          : new KJobEditError('Failed to lock job');

      if (error instanceof KJobEditLockedByOtherError) {
        dialogStore.open('jobEditLockedByOther', {
          lockedBy: error.lockedBy || 'another user',
        });
      } else if (error instanceof KJobEditLockNotFoundError) {
        dialogStore.open('jobEditNotFound', {
          errorMessage: 'Job not found',
        });
      }

      throw jobEditError;
    } finally {
      state.isLocking = false;
    }
  }

  async function unlockJob(): Promise<void> {
    if (!state.lockState.isLocked || !state.jobId || !state.jobDate) {
      return;
    }

    try {
      await fetchers.fetchPostJobEditUnlock({
        job_id: state.jobId,
        job_date: state.jobDate,
      });

      Object.assign(state.lockState, {
        isLocked: false,
        lockedBy: null,
        lockTimestamp: null,
        isCurrentSection: false,
        currentSection: null,
      });
    } catch (error) {
      const jobEditError =
        error instanceof KJobEditError
          ? error
          : new KJobEditError('Failed to unlock job');

      state.error = jobEditError;
      throw jobEditError;
    } finally {
      state.isLocking = false;
    }
  }

  function resetLock(): void {
    Object.assign(state.lockState, {
      isLocked: false,
      lockedBy: null,
      lockTimestamp: null,
    });
  }

  function setCurrentSection(sectionName: string | null): void {
    state.lockState.currentSection = sectionName;
    state.lockState.isCurrentSection = sectionName !== null;
  }

  function handleOverlayClick(event: MouseEvent): void {
    if (
      event.target === event.currentTarget &&
      state.lockState.isCurrentSection &&
      state.lockState.currentSection
    ) {
      state.overlayFeedback.sectionName = state.lockState.currentSection;
      state.overlayFeedback.tick += 1;
    }
  }

  async function loadJob(silent = false): Promise<void> {
    if (!state.jobId || !state.jobDate) {
      const error = new KJobEditError(
        'Cannot load job: job ID or date is missing',
      );
      throw error;
    }

    const requestId = ++loadJobRequestId;
    const requestedJobId = state.jobId;
    const requestedJobDate = state.jobDate;

    try {
      if (!silent) {
        state.isLoading = true;
      }

      const response = await fetchers.fetchGetJobEditDetails({
        job_id: requestedJobId,
        job_date: requestedJobDate,
      });

      if (requestId !== loadJobRequestId) {
        return;
      }

      if (
        state.jobId !== requestedJobId ||
        state.jobDate !== requestedJobDate
      ) {
        return;
      }

      state.jobData = response;
      state.originalJobData = cloneDeep(response);
      state.error = null;

      if (!silent) {
        const customerCode = state.jobData.customer;
        if (customerCode) {
          addressBookStore.fetch(customerCode).catch(() => {
            // Toast is shown by fetchGetLookupAddress; this swallows the
            // rethrow so the fire-and-forget call doesn't surface an
            // unhandled rejection.
          });
        }
      }
    } catch (error) {
      if (requestId !== loadJobRequestId) {
        return;
      }

      if (
        state.jobId !== requestedJobId ||
        state.jobDate !== requestedJobDate
      ) {
        return;
      }

      const jobEditError =
        error instanceof Error
          ? error
          : new KJobEditError('Failed to load job data');

      state.error = jobEditError;
      throw jobEditError;
    } finally {
      if (requestId === loadJobRequestId && !silent) {
        state.isLoading = false;
      }
    }
  }

  async function executeWithLock<T>(operation: () => Promise<T>): Promise<T> {
    await lockJob();

    try {
      const result = await operation();
      return result;
    } finally {
      await unlockJob();
    }
  }

  function swapStops(stopNumberA: number, stopNumberB: number): void {
    if (!state.jobData?.stops) return;
    const stops = state.jobData.stops;
    const idxA = stops.findIndex((s) => s.stop_number === stopNumberA);
    const idxB = stops.findIndex((s) => s.stop_number === stopNumberB);
    if (idxA === -1 || idxB === -1) return;
    stops[idxA].stop_number = stopNumberB;
    stops[idxB].stop_number = stopNumberA;
    stops.sort((a, b) => a.stop_number - b.stop_number);
  }

  function removeStop(stopNumber: number): void {
    if (!state.jobData?.stops) return;
    const idx = state.jobData.stops.findIndex(
      (s) => s.stop_number === stopNumber,
    );
    if (idx !== -1) {
      state.jobData.stops.splice(idx, 1);
    }
  }

  function addStop(): void {
    if (!state.jobData?.stops) return;
    if (state.jobData.stops.length >= 3) return;

    const stop2 = state.jobData.stops[1];
    const emptyStop = {
      ...createEmptyStopItem(),
      stop_number: 3,
      state: stop2.state,
      time_earliest: stop2.time_earliest,
      time_latest: stop2.time_latest,
    };

    state.jobData.stops.push(emptyStop);
  }

  function restoreStops(): void {
    if (!state.jobData || !state.originalJobData) return;
    state.jobData.stops = cloneDeep(state.originalJobData.stops);
  }

  // can_schedule is carried on stop 1 by convention (header and other
  // readers mirror this assumption).
  function setCanSchedule(value: boolean): void {
    if (!state.jobData?.stops[0]) return;
    // can_schedule is readonly on the API type but is edited locally in jobedit
    (state.jobData.stops[0] as { can_schedule: boolean }).can_schedule = value;
  }

  function $reset(): void {
    privateStore.$reset();
    addressBookStore.$reset();
  }

  function setMockData(data: JobData): void {
    state.jobData = data;
    state.originalJobData = cloneDeep(data);
  }

  function setMockLockState(lockState: Partial<JobEditLockState>): void {
    Object.assign(state.lockState, lockState);
  }

  return {
    jobId,
    jobDate,
    jobData,
    originalJobData,
    isLoading,
    isLocking,
    lockState: computed(() => state.lockState),
    overlayFeedback: computed(() => state.overlayFeedback),
    hasChanges,
    init,
    lockJob,
    unlockJob,
    resetLock,
    executeWithLock,
    setCurrentSection,
    handleOverlayClick,
    loadJob,
    swapStops,
    removeStop,
    addStop,
    restoreStops,
    setCanSchedule,
    setOnDataChanged,
    notifyDataChanged,
    setMockData,
    setMockLockState,
    $reset,
  };
});
